import ITag from '../../../components/ITag'
import {toast} from "react-toastify";
import {useRef, useEffect, useState} from "react";
import { get_permissions } from "../../../utils/index.js";
import { modifyProfile, changePassword } from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { parseSessionToken } from "../../../lib/window-shim";

/**
 * SecSettings component.
 *
 * Modal dialog for editing profile settings.
 * Allows modification of:
 * - Bio/description
 * - Location
 * - Role (admin only)
 * - Social media links (dropdown selector + URL)
 * - Email notification preferences
 * - Profile visibility (public/private)
 * - Social links visibility toggle
 *
 * Uses Backend 'modifyProfile' to persist changes.
 * Shows quick options dropdown (Live Stream, Change Password).
 *
 * @param {Object} props - Component props
 * @param {Function} props.ssm - Set modal show state
 * @param {Object} props.edp - Profile data being edited (from parent state)
 * @param {Function} props.spi - Set profile info callback (updates parent)
 * @returns {React.ReactElement} Settings form modal
 */
function SecSettings({ssm, edp, spi}) {

    const rawToken = parseSessionToken();
    let orig = rawToken ? JSON.parse(rawToken) : null;

    // Initialize form state from edp
    const [profileVisibility, setProfileVisibility] = useState(edp?.profile_visibility || 'public');
    // Initialize socials visibility from edp (defaults to true for backward compatibility)
    const [showSocials, setShowSocials] = useState(() => {
        if (edp?.show_socials !== undefined) {
            return Boolean(edp.show_socials);
        }
        return true;
    });

    // Track selected social platform
    const [selectedPlatform, setSelectedPlatform] = useState('twitter');
    const socLinkInputRef = useRef(null);

    // Password change state
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    // Helper: get social links as object from edp.socials (handles both string and object)
    const getSocialsObject = () => {
        if (edp?.socials) {
            try {
                return typeof edp.socials === 'string' ? JSON.parse(edp.socials) : edp.socials;
            } catch (e) {
                console.warn('Failed to parse socials');
                return {};
            }
        }
        return {};
    };

    // Populate input when platform changes or component mounts
    useEffect(() => {
        const input = socLinkInputRef.current;
        if (input) {
            const socials = getSocialsObject();
            input.value = socials[selectedPlatform] || "";
        }
    }, [selectedPlatform, edp?.socials]);

    // Populate location and role fields on mount/edp change
    useEffect(() => {
        if(edp) {
            const locField = document.getElementById('loc');
            const roleField = document.getElementById('role');
            if (locField) locField.value = edp?.location || '';
            if (roleField) roleField.value = edp?.role || '';
        }
    }, [edp]);

    async function handleSubmit() {
        let lloc = document.getElementById("loc").value;
        let lbio = document.getElementById("bio").value;
        let lrol = document.getElementById("role");
        let send = {}

        if (lloc !== orig.location) {
            send.location = lloc;
        }
        if (lbio !== orig.bio) {
            send.bio = lbio;
        }
        if (lrol?.value !== orig.role && lrol) {
            send.role = lrol.value;
        }

        // Include profile_visibility if changed
        if (profileVisibility !== edp?.profile_visibility) {
            send.profile_visibility = profileVisibility;
        }

        // Include show_socials if changed
        if (showSocials !== edp?.show_socials) {
            send.show_socials = showSocials;
        }

        // Include socials (already updated in edp.socials via updateSocials)
        if (edp?.socials) {
            try {
                send.socials = typeof edp.socials === 'string'
                    ? JSON.parse(edp.socials)
                    : edp.socials;
            } catch (e) {
                console.warn('Failed to parse socials, sending as empty object');
                send.socials = {};
            }
        } else {
            send.socials = {};
        }

        // If editing another user as admin
        if (edp?.id.toString() !== orig?.account?.id.toString()) {
            send.editFor = edp.id.toString();
        }

        try {
            const result = await modifyProfile(send)
            if (result?.error) {
                toast.error("Problem updating your settings, please report to admin")
            } else {
                // Update local profile state
                const updatedProfile = {...edp, ...send};
                spi(updatedProfile);
                toast.success("Your profile has been updated")
            }
        } catch (error) {
            console.error('Failed to update profile:', error)
            toast.error("Failed to update profile. Please try again.")
        }
    }

    function QuickOptionsMenu() {
        return (
            <div className="dropdown">
                <button
                    className="btn btn-sm btn-outline-light dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                >
                    Quick Options
                </button>
                <ul className="dropdown-menu">
                    <li><a className="dropdown-item" href="#">Live Stream</a></li>
                    <li>
                        <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => setShowPasswordForm(!showPasswordForm)}
                        >
                            Change Password
                        </button>
                    </li>
                </ul>
            </div>
        );
    }

    function updateSocials() {
        const input = socLinkInputRef.current;
        if (!input) return;

        try {
            const socials = getSocialsObject();
            socials[selectedPlatform] = input.value;
            edp.socials = JSON.stringify(socials);
        } catch (e) {
            toast.warn("Invalid data in social info, social links have been cleared.");
            edp.socials = JSON.stringify({});
        }
    }

    async function handlePasswordChange() {
        // Validation
        if (!newPassword) {
            toast.error("Please enter a new password");
            return;
        }
        if (newPassword.length < 6) {
            toast.error("New password must be at least 6 characters");
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }

        setIsChangingPassword(true);

        try {
            const isAdmin = get_permissions('admin');
            const isEditingSelf = edp?.id?.toString() === orig?.account?.id?.toString();

            // Build request payload
            const payload = { newPassword };

            // If admin editing another user, use editFor
            if (isAdmin && !isEditingSelf) {
                payload.editFor = edp?.id;
            } else {
                // User changing own password requires current password
                if (!currentPassword) {
                    toast.error("Please enter your current password");
                    setIsChangingPassword(false);
                    return;
                }
                payload.currentPassword = currentPassword;
            }

            const result = await changePassword(payload);

            if (result?.error) {
                toast.error(result.response?.output || "Failed to change password. Please try again.");
            } else {
                toast.success("Password changed successfully");
                // Reset form
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setShowPasswordForm(false);
            }
        } catch (error) {
            console.error('Failed to change password:', error);
            const errorMessage = error?.message || "Failed to change password. Please try again.";
            toast.error(errorMessage);
        } finally {
            setIsChangingPassword(false);
        }
    }

    const notificationOptions = [
        { id: 'comments', text: 'Comments on your items', checked: true },
        { id: 'bids', text: 'Bids on your items', checked: true },
        { id: 'offers', text: 'Offers on your items', checked: false },
        { id: 'sales', text: 'Item sold', checked: true },
        { id: 'transfers', text: 'Item transferred', checked: false },
    ];

    return (
        edp && (
            <div className="p-3">

                {/* Bio Section */}
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="text-uppercase text-body text-xs fw-bold mb-0">About Me / Bio</h6>
                        <QuickOptionsMenu />
                    </div>
                    <textarea
                        rows="6"
                        id="bio"
                        className="input-purple w-100"
                    >{edp?.bio || ''}</textarea>
                </div>

                {/* Location */}
                <div className="mb-3">
                    <h6 className="text-uppercase text-body text-xs fw-bold mb-2">Location</h6>
                    <input
                        id="loc"
                        className="input-purple w-100"
                        placeholder="Enter your location"
                    />
                </div>

                {/* Role (Admin only) */}
                {get_permissions('admin') && (
                    <div className="mb-3">
                        <h6 className="text-uppercase text-body text-xs fw-bold mb-2">Role</h6>
                        <input
                            id="role"
                            className="input-purple w-100"
                            placeholder="Enter role"
                        />
                    </div>
                )}

                {/* Profile Visibility */}
                <div className="mb-3">
                    <h6 className="text-uppercase text-body text-xs fw-bold mb-2">Profile Visibility</h6>
                    <select
                        id="profile_visibility"
                        className="input-purple w-100"
                        value={profileVisibility}
                        onChange={(e) => setProfileVisibility(e.target.value)}
                    >
                        <option value="public">Public</option>
                        <option value="private">Private</option>
                    </select>
                </div>

                {/* Social Links Visibility */}
                <div className="mb-4">
                    <h6 className="text-uppercase text-body text-xs fw-bold mb-2">Social Links Visibility</h6>
                    <small className="text-muted d-block mb-2">
                        Control who can see your social media links on your profile.
                    </small>
                    <div className="form-check form-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="show_socials"
                            checked={showSocials}
                            onChange={(e) => setShowSocials(e.target.checked)}
                            style={{cursor: 'pointer'}}
                        />
                        <label className="form-check-label" htmlFor="show_socials">
                            Show to everyone (including logged-out visitors)
                        </label>
                    </div>
                    <small className="text-muted d-block">
                        When OFF, only you and logged-in users can see your social links.
                    </small>
                </div>

                {/* Social Links */}
                <div className="mb-4">
                    <h6 className="text-uppercase text-body text-xs fw-bold mb-2">Socials / External Links</h6>
                    <div className="row g-2 mb-2">
                        <div className="col-4">
                            <select
                                id="elSelect"
                                value={selectedPlatform}
                                onChange={(e) => {
                                    // Save current platform's value before switching
                                    updateSocials();
                                    setSelectedPlatform(e.target.value);
                                }}
                                className="input-purple w-100"
                            >
                                <option value="discord">Discord</option>
                                <option value="twitter">Twitter</option>
                                <option value="facebook">Facebook</option>
                                <option value="instagram">Instagram</option>
                                <option value="matrix">Matrix</option>
                                <option value="web">Website</option>
                            </select>
                        </div>
                        <div className="col-8">
                            <input
                                ref={socLinkInputRef}
                                id="socLink"
                                onBlur={() => updateSocials()}
                                className="input-purple w-100"
                                placeholder="Enter URL (without https://)"
                            />
                        </div>
                    </div>
                </div>

                {/* Password Change Section */}
                {showPasswordForm && (
                    <div className="mb-4 border-top pt-3" style={{borderColor: 'rgba(255,255,255,0.2)'}}>
                        <h6 className="text-uppercase text-body text-xs fw-bold mb-3">Change Password</h6>

                        {/* Current Password (only for self-change) */}
                        {(!get_permissions('admin') || edp?.id?.toString() === orig?.account?.id?.toString()) && (
                            <div className="mb-3">
                                <label htmlFor="currentPassword" className="form-label text-light text-xs fw-bold mb-1">
                                    Current Password
                                </label>
                                <input
                                    type="password"
                                    id="currentPassword"
                                    className="input-purple w-100"
                                    placeholder="Enter your current password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    disabled={isChangingPassword}
                                />
                            </div>
                        )}

                        {/* New Password */}
                        <div className="mb-3">
                            <label htmlFor="newPassword" className="form-label text-light text-xs fw-bold mb-1">
                                New Password
                            </label>
                            <input
                                type="password"
                                id="newPassword"
                                className="input-purple w-100"
                                placeholder="Enter new password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={isChangingPassword}
                            />
                            <small className="text-muted d-block mt-1">
                                Minimum 6 characters
                            </small>
                        </div>

                        {/* Confirm New Password */}
                        <div className="mb-3">
                            <label htmlFor="confirmPassword" className="form-label text-light text-xs fw-bold mb-1">
                                Confirm New Password
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                className="input-purple w-100"
                                placeholder="Re-enter new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={isChangingPassword}
                            />
                        </div>

                        {/* Password Change Actions */}
                        <div className="d-flex gap-2 mt-3">
                            <button
                                onClick={handlePasswordChange}
                                disabled={isChangingPassword}
                                className="btn btn-success text-bolder text-light"
                                style={{minWidth: '120px'}}
                            >
                                {isChangingPassword ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Changing...
                                    </>
                                ) : (
                                    'Change Password'
                                )}
                            </button>
                            <button
                                onClick={() => {
                                    setShowPasswordForm(false);
                                    setCurrentPassword('');
                                    setNewPassword('');
                                    setConfirmPassword('');
                                }}
                                disabled={isChangingPassword}
                                className="btn btn-outline-light text-bolder"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {/* Email Notifications */}
                <div className="mb-4">
                    <h6 className="text-uppercase text-body text-xs fw-bold mb-3">Email Notifications</h6>
                    <div className="row">
                        <div className="col-6">
                            <ul className="list-group list-group-flush">
                                {notificationOptions.slice(0, 3).map((item) => (
                                    <li key={item.id} className="list-group-item bg-transparent px-0 py-2">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={item.id}
                                                defaultChecked={item.checked}
                                            />
                                            <label className="form-check-label text-light text-sm" htmlFor={item.id}>
                                                {item.text}
                                            </label>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="col-6">
                            <ul className="list-group list-group-flush">
                                {notificationOptions.slice(3).map((item) => (
                                    <li key={item.id} className="list-group-item bg-transparent px-0 py-2">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={item.id}
                                                defaultChecked={item.checked}
                                            />
                                            <label className="form-check-label text-light text-sm" htmlFor={item.id}>
                                                {item.text}
                                            </label>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex flex-column gap-2">
                    <button
                        onClick={() => {
                            // Save any pending changes before closing
                            updateSocials();
                            handleSubmit();
                            ssm(false);
                        }}
                        className="btn btn-success text-bolder text-light w-100"
                    >
                        Update
                    </button>
                    <button
                        onClick={() => {
                            ssm(false);
                        }}
                        className="btn btn-outline-light text-bolder w-100"
                    >
                        Close
                    </button>
                </div>
            </div>
        )
    )
}

export default SecSettings
